import React from 'react';
import { Button, Input, Modal, Space, Tag, Tooltip, message } from 'antd';
import { CopyOutlined, MailOutlined } from '@ant-design/icons';

import { ancanaApi } from '../../../api/ancanaApi';
import { IMembership } from '../../../types/models/tenancy';

const PATH = 'tenancy/memberships';

/**
 * Show a fresh console invite link to hand over by hand — in a call, a chat.
 *
 * The invite email only goes out in production, so off it this is the only way
 * in for a new tenant's admin. Each call issues a new single-use token; earlier
 * links, the emailed one included, keep working until one is used or expires.
 */
export const showStaffInviteLink = async (membershipId: number, title = 'Console invite link') => {
  try {
    const { data } = await ancanaApi.post(`${PATH}/${membershipId}/invite-link/`);
    Modal.info({
      title,
      width: 650,
      content: (
        <div style={{ paddingTop: 8 }}>
          <p>
            Opens the Caim console, where they set a password and land on their setup checklist.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input value={data.url} readOnly />
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(data.url);
                message.success('Invite link copied.');
              }}
            />
          </div>
          <p style={{ marginTop: 8 }}><small>Single use, expires in 14 days.</small></p>
        </div>
      ),
    });
  } catch (e: any) {
    if (e?.response?.status === 409) message.info('They already set a password; they can sign in.');
    else message.error('Could not create the invite link.');
  }
};

const resend = async (membership: IMembership) => {
  try {
    await ancanaApi.post(`${PATH}/${membership.id}/resend-invite/`);
    message.success(`A fresh invite is on its way to ${membership.user_email} (production only).`);
  } catch (e: any) {
    if (e?.response?.status === 409) message.info('They already set a password; they can sign in.');
    else message.error('Could not resend the invite.');
  }
};

/** The Staff table's Invite column: open invites get a link and a resend. */
export const StaffInviteCell = ({ membership }: { membership: IMembership }) => {
  if (!membership.invite_pending) return <Tag color="green">Signed up</Tag>;
  return (
    <Space size={4}>
      <Tag color="orange">Invited</Tag>
      <Tooltip title="Copy invite link">
        <Button size="small" icon={<CopyOutlined />} onClick={() => showStaffInviteLink(membership.id)} />
      </Tooltip>
      <Tooltip title="Resend invite email">
        <Button size="small" icon={<MailOutlined />} onClick={() => resend(membership)} />
      </Tooltip>
    </Space>
  );
};
